import React, { useEffect, useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import './PersonnelDialogStyle.scss';
import RHFTextArea from 'src/components/controls/tempRHFControls/RHFTextArea';
import useFetch from 'use-http';
import PersonnelPerformanceComment from 'src/models/personnel/PersonnelPerformanceComment';
import PersonnelToast from '../components/PersonnelToast';
import moment from 'moment';
import TSDILandman from 'src/models/personnel/TSDILandman';

interface IAddPersonnelDialogProps {
    dialogName: string;
    selectedPersonnel: TSDILandman;
    hideDialog: () => void;
}

const PersonnelCommentDialog = (props: IAddPersonnelDialogProps) => {
    const { handleSubmit, register, reset, getValues, setValue } = useForm();
    const { get, post, put, delete: remove, response } = useFetch();
    const [toast, setToast] = useState<{ message: string; color?: string, persistent?: boolean; } | null>(null);
    const [personnelPerformanceComments, setPersonnelPerformanceComments] = useState<Array<PersonnelPerformanceComment>>();

    const appContext = useContext<IAppContextState>(AppContext);
    const currentUser = {
        name: appContext.currentUser.username,
        id: appContext.currentUser.userId.toString(),
        lmsNumber: appContext.currentUser.lmsNumber?.toString()
    };

    useEffect(() => {
        onCreate();

        return () => {
            onDestroy();
        };
    }, []);

    useEffect(() => {
        if (props.selectedPersonnel) {
            getPersonnelComments();
        }
    }, [props.selectedPersonnel]);

    useEffect(() => {
        if (personnelPerformanceComments?.length > 0) {
            reset({ comment: personnelPerformanceComments });
        }
    }, [personnelPerformanceComments]);

    const onCreate = () => {
        console.log('PersonnelCommentDialog onCreate()');
    };

    const onDestroy = () => {
        console.log('PersonnelCommentDialog onDestroy()');
    };

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, persistent, color  });
    };

    // auto fill name if employee number is entered
    const getByLMSNumber = async () => {
        let lmsNumber = getValues('mgrNo');
        if (lmsNumber.length >= 3) {
            let result = await get(`/v1.0/landmen/personnel/${lmsNumber}/get-name-by-lms`);
            if (response.ok) {
                if (result !== '') {
                    setValue('mgrName', result);
                    setValue('mgrNo', lmsNumber);
                } else {
                    showToast('Unable to find valid name with provided lms number: ' + lmsNumber, `rgba(255,0,0,0.5)`);
                }
            } else {
                showToast('Unable to find user with provided lms number: ' + lmsNumber, `rgba(255,0,0,0.5)`);
            }
        }
    };

    const getPersonnelComments = async () => {
        let result = await get(`/v1.0/landmen/personnel/${props.selectedPersonnel?.tsdNo}/get-performance-comments`);
        if (response.ok) {
            if (result) {
                result?.forEach((comment: PersonnelPerformanceComment) => {
                    comment.mgrName = comment.mgrName ?? 'No manager record found';
                    comment.noteDate = comment.noteDate ? (moment(comment.noteDate).format('YYYY-MM-DD') as unknown as Date) : null;
                });
                
                setPersonnelPerformanceComments(result);
            }
        } else {
            console.log('error with get: ', response);
        }
    };

    const handleUpdateRow = async (index: number) => {
        let existingData = getValues();
        let commentRowData = { ...existingData.comment[index], commentId: personnelPerformanceComments[index].commentId };

        commentRowData.lmNo = props.selectedPersonnel.lmNo;
        commentRowData.mgrNo = parseInt(commentRowData.mgrNo.toString(), 10);
        commentRowData.noteDate = commentRowData.noteDate ? commentRowData.noteDate : new Date();

        let result = await put(`/v1.0/landmen/personnel/update-performance-comment`, commentRowData);

        if (response.ok) {
            showToast('Performance comment updated successfully');
            await getPersonnelComments();
        } else {
            console.log('error with put: ', response);
            showToast('Unable to update performance comment: ' + result.message, `rgba(255,0,0,0.5)`);
        }
    };

    const handleDeleteRow = async (index: number) => {
        let existingData = getValues();
        let commentRowData = { ...existingData.comment[index], commentId: personnelPerformanceComments[index].commentId };

        let result = await remove(`/v1.0/landmen/personnel/${commentRowData.commentId}/delete-performance-comment`);

        if (response.ok) {
            console.log('delete response: ', result);
            showToast('Performance comment deleted successfully');
            await getPersonnelComments();
        } else {
            console.log('error with post: ', response);
            showToast('Unable to delete performance comment: ' + result.message, `rgba(255,0,0,0.5)`);
        }
    };

    const onSubmit = async (data: PersonnelPerformanceComment) => {
        data.lmNo = props.selectedPersonnel.lmNo;
        data.mgrNo = parseInt(data.mgrNo.toString(), 10);
        data.noteDate = data.noteDate ? data.noteDate : new Date();

        let result = await post(`/v1.0/landmen/personnel/create-performance-comment`, data);
        if (response.ok) {
            console.log('post response: ', result);
            showToast('Performance comment created successfully');
            await getPersonnelComments();
        } else {
            console.log('error with post: ', response);
            showToast('Unable to create performance comment: ' + result.message, `rgba(255,0,0,0.5)`);
        }
    };

    // new row component
    const NewCommentRow = () => {
        return (
            <tr style={{ width: '100%', gap: '1rem', marginBottom: '1rem' }}>
                <td style={{ width: '5%', verticalAlign: 'bottom' }}>
                    <RHFInputBox
                        style={{ width: '100%' }}
                        register={register}
                        name="mgrNo"
                        onChange={() => getByLMSNumber()}
                        defaultVal={currentUser.lmsNumber}
                    />
                </td>
                <td style={{ width: '10%', verticalAlign: 'bottom' }}>
                    <RHFInputBox style={{ width: '100%' }} register={register} name="mgrName" defaultVal={currentUser.name} />
                </td>
                <td style={{ width: '10%', verticalAlign: 'bottom' }}>
                    <RHFInputBox style={{ width: '100%' }} type="date" register={register} name="noteDate" defaultVal={new Date().toISOString().split('T')[0]} />
                </td>
                <td style={{ width: '65%', verticalAlign: 'bottom' }}>
                    <RHFTextArea style={{ width: '100%', height: '15%' }} register={register} name="note" />
                </td>
            </tr>
        );
    };

    return (
        <Dialog title="Performance Comments" dialogName={props.dialogName} bodyStyle={{ width: '75%', height: '75%', overflowY: 'auto' }}>
            <div>
                {toast && (
                    <PersonnelToast
                        message={toast.message}
                        color={toast.color}
                        onClose={() => setToast(null)}
                        persistent={toast.persistent}
                    />
                )}
            </div>
            <div>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <table style={{ width: '100%',  borderSpacing: '15px' }} id="commentsTable">
                        <thead>
                            <tr>
                                <th style={{ width: '10%' }}>Manager / HR</th>
                                <th style={{ width: '10%' }}>Name</th>
                                <th style={{ width: '10%' }}>Comment Date</th>
                                <th style={{ width: '60%' }}>Comment</th>
                                <th style={{ width: '10%' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {personnelPerformanceComments && personnelPerformanceComments.length > 0 ? (
                                <>
                                    {personnelPerformanceComments.map((comment, index) => (
                                        <tr style={{ width: '100%', gap: '1rem', marginBottom: '1rem' }} key={index}>
                                            <td style={{ width: '5%', verticalAlign: 'bottom' }}>
                                                <RHFInputBox
                                                    register={register}
                                                    style={{ width: '100%' }}
                                                    name={`comment.${index}.mgrNo`}
                                                    defaultVal={comment.mgrNo?.toString()}
                                                />
                                            </td>
                                            <td style={{ width: '10%', verticalAlign: 'bottom' }}>
                                                <RHFInputBox
                                                    register={register}
                                                    style={{ width: '100%' }}
                                                    name={`comment.${index}.mgrName`}
                                                    defaultVal={comment.mgrName}
                                                    disabled={true}
                                                />
                                            </td>
                                            <td style={{ width: '10%', verticalAlign: 'bottom' }}>
                                                <RHFInputBox
                                                    register={register}
                                                    style={{ width: '100%' }}
                                                    type="date"
                                                    name={`comment.${index}.noteDate`}
                                                />
                                            </td>
                                            <td style={{ width: '65%', verticalAlign: 'bottom' }}>
                                                <RHFTextArea
                                                    register={register}
                                                    style={{ width: '100%', height: '15%' }}
                                                    name={`comment.${index}.note`}
                                                    defaultValue={comment.note}
                                                />
                                            </td>
                                            <td style={{ width: '10%', verticalAlign: 'bottom', paddingLeft: '2%' }}>
                                                <div className="form-row">
                                                    <a onClick={() => handleUpdateRow(index)}>
                                                        <i style={{ paddingRight: '1%', fontSize: '2.5rem' }} className="far fa-save mr-1 clickable" />
                                                    </a>
                                                    <a onClick={() => handleDeleteRow(index)}>
                                                        <i style={{ fontSize: '2.5rem' }} className="far fa-trash mr-1 clickable" />
                                                    </a>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    <NewCommentRow />
                                </>
                            ) :
                                <NewCommentRow />}
                        </tbody>
                    </table>
                    <div className="form-row" style={{ marginTop: '2rem', marginLeft: '33.5%', textAlign: 'center' }}>
                        <Button
                            className="blue-hover-button"
                            style={{ width: '24%', marginRight: '1rem' }}
                            text={'Add'}
                            type="submit"
                            intent="primary"
                        />
                        <Button
                            className="blue-hover-button"
                            style={{ width: '24%', marginRight: '1rem' }}
                            text={'Close'}
                            onClick={() => props.hideDialog()}
                        />
                    </div>
                </form>
            </div>
        </Dialog>
    );
};

export default PersonnelCommentDialog;


// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/PersonnelPerformanceDialog.tsx